import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiUsers, 
  FiDollarSign, 
  FiFileText, 
  FiAward,
  FiTrendingUp,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiSettings,
  FiX,
  FiBell
} from 'react-icons/fi';
import './DashboardKemahasiswaan.css';

const DashboardKemahasiswaan = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrganisasi: 0,
    totalAnggota: 0,
    totalDana: 0,
    danaDigunakan: 0,
    danaTersisa: 0,
    proposalAktif: 0,
    proposalMenunggu: 0,
    kegiatanBulanIni: 0,
    prestasiTahunIni: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('dashboardTheme') || 'terang');

  useEffect(() => {
    fetchDashboardData();
    fetchNotifications();
  }, []);

  useEffect(() => {
    // Apply theme on change
    document.body.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch stats kemahasiswaan (hanya Dana Kegiatan Mahasiswa)
      const statsResponse = await axios.get('/api/proposals/stats-kemahasiswaan', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update stats dengan data real dari API
      setStats({
        totalOrganisasi: 12, // TODO: Replace with real data
        totalAnggota: 450,
        totalDana: statsResponse.data.danaKemahasiswaan || 0,
        danaDigunakan: statsResponse.data.danaDigunakan || 0,
        danaTersisa: statsResponse.data.danaTersisa || 0,
        proposalAktif: statsResponse.data.totalProposalAktif || 0,
        proposalMenunggu: 3, // TODO: Add to API
        kegiatanBulanIni: 5, // TODO: Add to API
        prestasiTahunIni: 15 // TODO: Add to API
      });

      // Mock data untuk recent activities
      setRecentActivities([
        {
          id: 1,
          type: 'proposal',
          title: 'Proposal Festival Seni Mahasiswa disetujui',
          date: new Date().toISOString(),
          status: 'approved'
        },
        {
          id: 2,
          type: 'kegiatan',
          title: 'Seminar Kesehatan Mental telah dilaksanakan',
          date: new Date(Date.now() - 86400000).toISOString(),
          status: 'completed'
        },
        {
          id: 3,
          type: 'prestasi',
          title: 'Tim Futsal meraih Juara 1 Turnamen Antar PT',
          date: new Date(Date.now() - 172800000).toISOString(),
          status: 'achievement'
        }
      ]);

      // Mock data untuk upcoming events
      setUpcomingEvents([
        {
          id: 1,
          title: 'Rapat Koordinasi Ormawa',
          date: new Date(Date.now() + 86400000).toISOString(),
          location: 'Aula Utama'
        },
        {
          id: 2,
          title: 'Bakti Sosial ke Desa',
          date: new Date(Date.now() + 259200000).toISOString(),
          location: 'Desa Baramuli'
        },
        {
          id: 3,
          title: 'Turnamen Olahraga Mahasiswa',
          date: new Date(Date.now() + 604800000).toISOString(),
          location: 'Lapangan Kampus'
        }
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Gagal mengambil data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch proposal baru dengan status Pending
      const response = await axios.get('/api/proposals?status=Pending', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter hanya proposal Kegiatan Mahasiswa yang baru (dalam 7 hari terakhir)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const newProposals = response.data.filter(p => 
        p.jenis_proposal === 'Kegiatan Mahasiswa' && 
        new Date(p.createdAt) > sevenDaysAgo
      );

      // Format notifikasi
      const notifs = newProposals.map(p => ({
        id: p._id,
        type: 'proposal',
        title: 'Proposal Baru',
        message: `${p.judul} - ${p.pengusul.nama}`,
        time: p.createdAt,
        read: false,
        data: p
      }));

      // Tambah notifikasi dummy untuk demo
      const demoNotifs = [
        {
          id: 'demo-1',
          type: 'proposal',
          title: 'Proposal Baru',
          message: 'Seminar Nasional IT 2025 - Andi Wijaya',
          time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          read: false
        },
        {
          id: 'demo-2',
          type: 'event',
          title: 'Kegiatan Mendatang',
          message: 'Rapat Koordinasi Ormawa besok',
          time: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          read: false
        },
        {
          id: 'demo-3',
          type: 'prestasi',
          title: 'Prestasi Baru',
          message: 'Juara 1 Lomba Debat Regional',
          time: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          read: true
        }
      ];

      const allNotifs = [...notifs, ...demoNotifs];
      setNotifications(allNotifs);
      setUnreadCount(allNotifs.filter(n => !n.read).length);

    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = (notifId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notifId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleThemeChange = (theme) => {
    setCurrentTheme(theme);
    localStorage.setItem('dashboardTheme', theme);
    document.body.setAttribute('data-theme', theme);
    toast.success(`Tema ${theme} diterapkan!`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'proposal':
        return <FiFileText />;
      case 'kegiatan':
        return <FiCalendar />;
      case 'prestasi':
        return <FiAward />;
      default:
        return <FiCheckCircle />;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'completed':
        return 'info';
      case 'achievement':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-kemahasiswaan">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-kemahasiswaan">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-text">
            <h1 className="header-title">Dashboard Kemahasiswaan</h1>
            <p className="header-subtitle">Monitoring dan Manajemen Kegiatan Kemahasiswaan</p>
          </div>
          <div className="header-actions">
            {/* Notification Button */}
            <div className="notification-container">
              <button 
                className="notification-btn"
                onClick={() => setShowNotifications(!showNotifications)}
                title="Notifikasi"
              >
                <FiBell />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  <div className="notification-overlay" onClick={() => setShowNotifications(false)}></div>
                  <div className="notification-dropdown">
                    <div className="notification-header">
                      <h4>Notifikasi</h4>
                      {unreadCount > 0 && (
                        <button className="mark-all-read" onClick={markAllAsRead}>
                          Tandai semua dibaca
                        </button>
                      )}
                    </div>
                    <div className="notification-list">
                      {notifications.length === 0 ? (
                        <div className="notification-empty">
                          <FiBell />
                          <p>Tidak ada notifikasi</p>
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <div 
                            key={notif.id} 
                            className={`notification-item ${!notif.read ? 'unread' : ''}`}
                            onClick={() => markAsRead(notif.id)}
                          >
                            <div className={`notif-icon ${notif.type}`}>
                              {notif.type === 'proposal' && <FiFileText />}
                              {notif.type === 'event' && <FiCalendar />}
                              {notif.type === 'prestasi' && <FiAward />}
                            </div>
                            <div className="notif-content">
                              <h5>{notif.title}</h5>
                              <p>{notif.message}</p>
                              <span className="notif-time">{formatDateTime(notif.time)}</span>
                            </div>
                            {!notif.read && <div className="unread-dot"></div>}
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="notification-footer">
                        <button className="view-all-btn">Lihat Semua Notifikasi</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="header-date">
              <FiCalendar />
              <span>{formatDate(new Date().toISOString())}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Organisasi</p>
            <h3 className="stat-value">{stats.totalOrganisasi}</h3>
            <small className="stat-info">Ormawa & UKM</small>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Anggota</p>
            <h3 className="stat-value">{stats.totalAnggota}</h3>
            <small className="stat-info">Mahasiswa aktif</small>
          </div>
        </div>

        <div className="stat-card cyan">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Kemahasiswaan</p>
            <h3 className="stat-value">{formatCurrency(stats.totalDana)}</h3>
            <small className="stat-info">Total dana terkumpul</small>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <FiTrendingUp />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Digunakan</p>
            <h3 className="stat-value">{formatCurrency(stats.danaDigunakan)}</h3>
            <small className="stat-info">Untuk kegiatan</small>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Tersisa</p>
            <h3 className="stat-value">{formatCurrency(stats.danaTersisa)}</h3>
            <small className="stat-info">Saldo available</small>
          </div>
        </div>

        <div className="stat-card pink">
          <div className="stat-icon">
            <FiFileText />
          </div>
          <div className="stat-details">
            <p className="stat-label">Proposal Aktif</p>
            <h3 className="stat-value">{stats.proposalAktif}</h3>
            <small className="stat-info">{stats.proposalMenunggu} menunggu</small>
          </div>
        </div>

        <div className="stat-card indigo">
          <div className="stat-icon">
            <FiCalendar />
          </div>
          <div className="stat-details">
            <p className="stat-label">Kegiatan Bulan Ini</p>
            <h3 className="stat-value">{stats.kegiatanBulanIni}</h3>
            <small className="stat-info">Kegiatan terlaksana</small>
          </div>
        </div>

        <div className="stat-card yellow">
          <div className="stat-icon">
            <FiAward />
          </div>
          <div className="stat-details">
            <p className="stat-label">Prestasi Tahun Ini</p>
            <h3 className="stat-value">{stats.prestasiTahunIni}</h3>
            <small className="stat-info">Pencapaian mahasiswa</small>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="content-grid">
        {/* Recent Activities */}
        <div className="card activities-card">
          <div className="card-header">
            <h3><FiClock /> Aktivitas Terbaru</h3>
          </div>
          <div className="card-body">
            {recentActivities.length === 0 ? (
              <div className="empty-state">
                <p>Belum ada aktivitas</p>
              </div>
            ) : (
              <div className="activities-list">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className={`activity-item ${getActivityColor(activity.status)}`}>
                    <div className="activity-icon">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="activity-content">
                      <p className="activity-title">{activity.title}</p>
                      <span className="activity-date">{formatDateTime(activity.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="card events-card">
          <div className="card-header">
            <h3><FiCalendar /> Kegiatan Mendatang</h3>
          </div>
          <div className="card-body">
            {upcomingEvents.length === 0 ? (
              <div className="empty-state">
                <p>Tidak ada kegiatan mendatang</p>
              </div>
            ) : (
              <div className="events-list">
                {upcomingEvents.map((event) => (
                  <div key={event.id} className="event-item">
                    <div className="event-date">
                      <div className="event-day">
                        {new Date(event.date).getDate()}
                      </div>
                      <div className="event-month">
                        {new Date(event.date).toLocaleDateString('id-ID', { month: 'short' })}
                      </div>
                    </div>
                    <div className="event-content">
                      <h4 className="event-title">{event.title}</h4>
                      <p className="event-location">{event.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-card">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <button className="action-btn blue">
            <FiFileText />
            <span>Buat Proposal</span>
          </button>
          <button className="action-btn green">
            <FiCalendar />
            <span>Tambah Kegiatan</span>
          </button>
          <button className="action-btn orange">
            <FiAward />
            <span>Input Prestasi</span>
          </button>
          <button className="action-btn purple">
            <FiDollarSign />
            <span>Pencairan Dana</span>
          </button>
        </div>
      </div>

      {/* Floating Settings Button */}
      <button 
        className="floating-settings-btn"
        onClick={() => setShowSettings(!showSettings)}
        title="Pengaturan"
      >
        <FiSettings />
      </button>

      {/* Settings Panel */}
      {showSettings && (
        <>
          <div className="settings-overlay" onClick={() => setShowSettings(false)}></div>
          <div className="settings-panel">
            <div className="settings-header">
              <h3>Pengaturan Dashboard</h3>
              <button className="close-settings" onClick={() => setShowSettings(false)}>
                <FiX />
              </button>
            </div>
            <div className="settings-body">
              <div className="settings-section">
                <h4>Tampilan</h4>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    <span>Tampilkan Statistik</span>
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    <span>Tampilkan Aktivitas Terbaru</span>
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    <span>Tampilkan Kegiatan Mendatang</span>
                  </label>
                </div>
              </div>
              
              <div className="settings-section">
                <h4>Notifikasi</h4>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    <span>Notifikasi Proposal Baru</span>
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    <span>Notifikasi Kegiatan</span>
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" />
                    <span>Notifikasi Email</span>
                  </label>
                </div>
              </div>

              <div className="settings-section">
                <h4>Preferensi</h4>
                <div className="setting-item">
                  <label>Tema Dashboard</label>
                  <select 
                    className="setting-select"
                    value={currentTheme}
                    onChange={(e) => handleThemeChange(e.target.value)}
                  >
                    <option value="terang">☀️ Terang - Bersih dan Modern</option>
                    <option value="gelap">🌙 Gelap - Nyaman untuk Mata</option>
                    <option value="segar">🌿 Segar - Nuansa Alam</option>
                    <option value="futuristik">🚀 Futuristik - Tech & Neon</option>
                    <option value="modern">💎 Modern - Minimalis Elegan</option>
                  </select>
                  <small className="theme-description">
                    {currentTheme === 'terang' && '✨ Tema default dengan warna cerah dan kontras tinggi'}
                    {currentTheme === 'gelap' && '🌌 Mode gelap yang nyaman untuk mata di malam hari'}
                    {currentTheme === 'segar' && '🍃 Warna hijau segar yang menenangkan'}
                    {currentTheme === 'futuristik' && '⚡ Desain futuristik dengan aksen neon'}
                    {currentTheme === 'modern' && '💫 Desain minimalis dengan gradasi halus'}
                  </small>
                </div>
                <div className="setting-item">
                  <label>Bahasa</label>
                  <select className="setting-select">
                    <option value="id">Indonesia</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>

              <div className="settings-footer">
                <button className="btn-secondary" onClick={() => setShowSettings(false)}>
                  Tutup
                </button>
                <button className="btn-primary">
                  Simpan Pengaturan
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardKemahasiswaan;
